import { motion } from 'framer-motion';
import './GoldLeafFlash.css';

/**
 * Atmosphere A3 Slice A — Gold-leaf flash overlay.
 *
 * Radial-gradient gold flash that fires behind the QRCinematicResponse
 * for QR 1 (Excellent) and at reduced intensity for QR 2 (Very Good).
 * QR 3/4/Failure don't render this component at all.
 *
 * Three keyframes via framer-motion: fade-in (0→peak), hold, fade-out.
 * `intensity` scales the peak opacity so QR 1's flash feels more
 * dramatic than QR 2's softer glow without forking the component.
 *
 * Voice mode bumps intensity slightly (per A3 design — voice cinematic
 * is more theatrical) but Slice A treats voice mode as a 1.0× passthrough;
 * Slice B will introduce the 1.5× flash extension explicitly.
 *
 * Sparkle particles deferred to Slice B polish.
 */
export interface GoldLeafFlashProps {
  /** Fired once the flash animation has fully settled. */
  onComplete: () => void;
  /** Peak opacity (0.0–1.0). Default 0.6 — QR 1 spec. */
  intensity?: number;
  /** Total animation duration in ms. Default 1500 — matches QR 1 sting. */
  durationMs?: number;
  /** Optional testid prefix; default 'br-gold-leaf-flash'. */
  testidPrefix?: string;
}

const DEFAULT_INTENSITY = 0.6;
const DEFAULT_DURATION_MS = 1500;

export function GoldLeafFlash({
  onComplete,
  intensity = DEFAULT_INTENSITY,
  durationMs = DEFAULT_DURATION_MS,
  testidPrefix = 'br-gold-leaf-flash',
}: GoldLeafFlashProps) {
  const clampedIntensity = Math.min(1, Math.max(0, intensity));
  const durationSeconds = durationMs / 1000;

  return (
    <div
      className="br-gold-leaf-flash-backdrop"
      data-testid={`${testidPrefix}-backdrop`}
      aria-hidden="true"
    >
      <motion.div
        className="br-gold-leaf-flash-radial"
        data-testid={testidPrefix}
        initial={{ opacity: 0, scale: 0.7 }}
        animate={{
          opacity: [0, clampedIntensity, clampedIntensity, 0],
          scale: [0.7, 1.05, 1.0, 1.0],
        }}
        transition={{
          duration: durationSeconds,
          times: [0, 0.25, 0.75, 1],
          ease: 'easeOut',
        }}
        onAnimationComplete={onComplete}
      />
    </div>
  );
}

export default GoldLeafFlash;
